import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDate } from "../lib/format";

const renderInline = (text) =>
  text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-forest underline" target="_blank" rel="noreferrer">$1</a>');

const renderMd = (text) =>
  text.split("\n").map((line, i) => {
    const imgM = line.trim().match(/^!\[(.*?)\]\((\S+)\)$/);
    if (imgM) return <img key={i} src={imgM[2]} alt={imgM[1]} className="w-full rounded-2xl my-4 border border-stone-100" />;
    const vidM = line.trim().match(/^\[video\]\((\S+)\)$/);
    if (vidM) return <video key={i} src={vidM[1]} controls preload="metadata" className="w-full rounded-2xl my-4 bg-black" />;
    if (line.startsWith("## ")) return <h3 key={i} className="font-serif text-xl text-forest mt-7" dangerouslySetInnerHTML={{ __html: renderInline(line.slice(3)) }} />;
    if (line.startsWith("- ")) return <li key={i} className="ml-5 list-disc mt-1.5" dangerouslySetInnerHTML={{ __html: renderInline(line.slice(2)) }} />;
    if (!line.trim()) return <div key={i} className="h-3" />;
    return <p key={i} dangerouslySetInnerHTML={{ __html: renderInline(line) }} />;
  });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true)); }, [slug]);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.seo_title || post.title} | Ata Angora Blog`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = post.seo_description || (post.content || "").replace(/[#*\[\]()]/g, "").slice(0, 155);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `https://ataangora.com/blog/${post.slug}`;
    let og = document.querySelector('meta[property="og:title"]');
    if (og) og.content = post.title;
    let ogImg = document.querySelector('meta[property="og:image"]');
    if (ogImg && post.image) ogImg.content = post.image;
    let s = document.getElementById("blogpost-jsonld");
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "blogpost-jsonld"; document.head.appendChild(s); }
    s.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: post.title, image: post.image,
      author: { "@type": "Organization", name: post.author || "Ata Angora" },
      publisher: { "@type": "Organization", name: "Ata Angora Doğal Gıdalar", logo: { "@type": "ImageObject", url: "https://ataangora.com/logo.jpg" } },
      datePublished: post.published_at, mainEntityOfPage: `https://ataangora.com/blog/${post.slug}`,
    });
  }, [post]);

  if (notFound) return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><p className="font-serif text-2xl">Yazı bulunamadı</p><Link to="/blog" className="text-forest font-semibold mt-4 inline-block">Blog'a dön</Link></div>;
  if (!post) return <div className="max-w-2xl mx-auto px-4 py-24 text-center text-stone-500">Yükleniyor...</div>;

  return (
    <div data-testid="blog-post-page" className="max-w-3xl mx-auto px-4 py-10">
      <nav className="text-sm text-stone-500"><Link to="/blog" className="hover:text-forest">Blog</Link> / <span className="text-stone-800">{post.category}</span></nav>
      <h1 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-4 leading-tight">{post.title}</h1>
      <p className="text-sm text-stone-500 mt-3">{post.author} · {fmtDate(post.published_at)}</p>
      <img src={post.image} alt={post.title} className="w-full rounded-3xl mt-8 aspect-[16/9] object-cover" />
      <article className="mt-8 text-stone-700 leading-relaxed text-base sm:text-lg">{renderMd(post.content)}</article>
      <div className="flex gap-2 mt-8 flex-wrap">
        {post.tags?.map((t) => <span key={t} className="text-xs bg-cream-dark text-stone-600 rounded-full px-3 py-1.5">#{t}</span>)}
      </div>
    </div>
  );
}
